import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getUploadUrl } from '../utils/api';
import { ArrowLeft, Download, X, Printer } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';

const ViewGradedPaper = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [exam, setExam] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const paperRef = useRef(null);
  const iframeRef = useRef(null);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  useEffect(() => {
    fetchData();
  }, [submissionId]);

  const fetchData = async () => {
    try {
      const [submissionRes, schoolRes] = await Promise.all([
        api.get(`/submissions/${submissionId}`),
        api.get('/school/info')
      ]);
      const examRes = await api.get(`/exams/${submissionRes.data.exam_id}`);
      
      setSubmission(submissionRes.data);
      setExam(examRes.data);
      setSchool(schoolRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load graded paper');
      navigate('/student/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getAnswer = (qIndex, pIndex) => {
    return submission?.answers?.find(
      (a) => a.question_idx === qIndex && a.part_idx === pIndex
    );
  };

  const getAnnotation = (qIndex, pIndex) => {
    return submission?.annotations?.find(
      (a) => a.question_idx === qIndex && a.part_idx === pIndex
    );
  };

  const downloadPDF = async () => {
    const loadingToast = toast.loading('Generating PDF, please wait...');
    
    try {
      const element = paperRef.current;
      
      if (!element) {
        throw new Error('Paper content not found. Please try again.');
      }
      
      // Wait a bit for any pending renders
      await new Promise(resolve => setTimeout(resolve, 500));
      
      
      // Capture with simpler options
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });
      
      
      // Convert to image
      const imgData = canvas.toDataURL('image/jpeg', 0.9);
      
      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate dimensions
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, '', 'FAST');
      heightLeft -= pdfHeight;
      
      // Add additional pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, '', 'FAST');
        heightLeft -= pdfHeight;
      }
      
      // Generate filename
      const examTitle = (exam?.title || 'Exam').replace(/[^a-z0-9]/gi, '_');
      const studentName = (submission?.student_name || 'Student').replace(/[^a-z0-9]/gi, '_');
      const fileName = `${examTitle}-${studentName}-Graded.pdf`;
      
      
      // Get PDF as Blob (more reliable for downloads)
      const pdfBlob = pdf.output('blob');
      
      // Create blob URL
      const blobUrl = URL.createObjectURL(pdfBlob);
      
      // Clean up previous blob URL if exists
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
      
      // Store PDF data and show modal
      setPdfBlobUrl(blobUrl);
      setPdfFileName(fileName);
      setShowPdfModal(true);
      
      toast.dismiss(loadingToast);
      toast.success('✅ PDF generated! Use the preview to print or save.', { duration: 4000 });
      
    } catch (err) {
      console.error('❌ PDF generation failed:', err);
      console.error('Error details:', err.message);
      
      toast.dismiss(loadingToast);
      
      let errorMsg = 'Failed to generate PDF. ';
      if (err.message.includes('not found')) {
        errorMsg += 'Content not ready. Please wait and try again.';
      } else if (err.message.includes('canvas')) {
        errorMsg += 'Problem capturing page. Try refreshing the page.';
      } else {
        errorMsg += err.message || 'Please try again.';
      }
      
      toast.error(errorMsg, { duration: 6000 });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-slate-500">Loading graded paper...</div>
      </div>
    );
  }

  if (!submission?.is_graded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">This paper hasn't been graded yet.</p>
          <button
            onClick={() => navigate('/student/dashboard')}
            className="px-6 py-2 bg-indigo-700 text-white rounded-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/student/dashboard')}
            className="flex items-center gap-2 text-slate-600 hover:text-indigo-700 transition-colors mb-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Graded Paper</h1>
              <p className="text-slate-600">{exam?.title}</p>
            </div>
            <button
              onClick={downloadPDF}
              className="flex items-center gap-2 px-6 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
            >
              <Download className="w-5 h-5" />
              Download PDF
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-6 md:p-8">
        {/* Grade Summary */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <p className="text-sm text-slate-600 mb-1">Your Grade</p>
              <p className="text-3xl font-bold text-indigo-700">{submission.grade}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">Marks Obtained</p>
              <p className="text-3xl font-bold text-slate-900">
                {submission.marks_allocated} / {exam.questions.reduce((sum, q) => sum + q.marks, 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">Percentage</p>
              <p className="text-3xl font-bold text-green-600">
                {Math.round((submission.marks_allocated / exam.questions.reduce((sum, q) => sum + q.marks, 0)) * 100)}%
              </p>
            </div>
          </div>
        </div>

        {/* Paper Content */}
        <div ref={paperRef} className="bg-white rounded-xl border border-slate-200 p-8">
          {/* School Header */}
          {school && (
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-200">
              {school.logo_url && (
                <img src={getUploadUrl(school.logo_url)} alt={school.name} className="w-16 h-16 rounded-lg object-cover" />
              )}
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{school.name}</h1>
                <p className="text-slate-600">Graded Examination Paper</p>
              </div>
            </div>
          )}
          
          <div className="text-center mb-8 border-b border-slate-200 pb-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{exam?.title}</h2>
            <p className="text-slate-600">Student: {submission.student_name}</p>
            <p className="text-slate-600">Grade: {submission.grade} | Marks: {submission.marks_allocated}</p>
          </div>

          {/* Questions */}
          {exam?.questions.map((question, qIndex) => (
            <div key={qIndex} className="mb-8 pb-8 border-b border-slate-200 last:border-0">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-xl font-semibold text-slate-900">
                  Question {qIndex + 1}
                </h3>
                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 font-medium rounded-full text-sm">
                  {question.marks} marks
                </span>
              </div>

              <p className="text-lg text-slate-700 mb-6">{question.question_text}</p>

              {/* Parts */}
              <div className="space-y-6">
                {question.parts.map((part, pIndex) => {
                  const answer = getAnswer(qIndex, pIndex);
                  const annotation = getAnnotation(qIndex, pIndex);
                  
                  return (
                    <div key={pIndex} className="p-6 bg-slate-50 rounded-lg">
                      <h4 className="font-semibold text-slate-900 mb-3">
                        Part {String.fromCharCode(97 + pIndex)}) {part.part_text}
                      </h4>

                      {/* Reference diagram for non-drawing parts */}
                      {part.diagram_base64 && !part.has_drawing && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-slate-700 mb-2">Reference Diagram:</p>
                          <img
                            src={part.diagram_base64}
                            alt="Reference diagram"
                            className="max-w-full rounded-lg border border-slate-200"
                          />
                        </div>
                      )}

                      {/* Student's Drawing with Teacher's Annotations */}
                      {answer?.drawing_data && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-slate-700 mb-2">Your Drawing with Teacher's Feedback:</p>
                          {annotation?.annotation_data ? (
                            <img
                              src={annotation.annotation_data}
                              alt="Annotated drawing"
                              className="max-w-full rounded-lg border-2 border-indigo-300"
                            />
                          ) : (
                            <img
                              src={answer.drawing_data}
                              alt="Your drawing"
                              className="max-w-full rounded-lg border border-slate-200"
                            />
                          )}
                        </div>
                      )}

                      {/* Teacher's Annotation on reference diagram (when student didn't draw) */}
                      {part.has_drawing && part.diagram_base64 && !answer?.drawing_data && annotation?.annotation_data && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-amber-600 mb-2">
                            Teacher's Feedback on Reference Diagram (you did not submit a drawing):
                          </p>
                          <img
                            src={annotation.annotation_data}
                            alt="Teacher's annotated diagram"
                            className="max-w-full rounded-lg border-2 border-amber-300"
                          />
                        </div>
                      )}

                      {/* Show original diagram if student didn't draw and teacher didn't annotate */}
                      {part.has_drawing && part.diagram_base64 && !answer?.drawing_data && !annotation?.annotation_data && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-amber-600 mb-2">
                            Reference Diagram (you did not submit a drawing):
                          </p>
                          <img
                            src={part.diagram_base64}
                            alt="Reference diagram"
                            className="max-w-full rounded-lg border border-amber-200"
                          />
                        </div>
                      )}

                      {/* Message when no drawing submitted and no diagram available */}
                      {part.has_drawing && !part.diagram_base64 && !answer?.drawing_data && (
                        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-amber-700 text-sm">
                            You did not submit a drawing for this part.
                          </p>
                        </div>
                      )}

                      {/* Text Answer */}
                      {answer?.text_answer && (
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm font-medium text-slate-700 mb-2">Your Answer:</p>
                            <div className="p-4 bg-white rounded-lg border border-slate-200">
                              <p className="text-slate-700 whitespace-pre-wrap">{answer.text_answer}</p>
                            </div>
                          </div>
                          
                          {/* Teacher's Feedback */}
                          {annotation?.text_feedback && (
                            <div>
                              <p className="text-sm font-medium text-red-600 mb-2">Teacher's Feedback:</p>
                              <div className="p-4 bg-red-50 rounded-lg border-2 border-red-200">
                                <p className="text-red-700 whitespace-pre-wrap">{annotation.text_feedback}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPdfModal && pdfBlobUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-5xl h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                PDF Preview - {pdfFileName}
              </h3>
              <div className="flex items-center gap-3">
                <a
                  href={pdfBlobUrl}
                  download={pdfFileName}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </a>
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
                <button
                  onClick={() => {
                    setShowPdfModal(false);
                    if (pdfBlobUrl) {
                      URL.revokeObjectURL(pdfBlobUrl);
                      setPdfBlobUrl(null);
                    }
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            
            {/* PDF Iframe */}
            <div className="flex-1 p-4 bg-slate-100 overflow-auto">
              <iframe
                ref={iframeRef}
                src={pdfBlobUrl}
                type="application/pdf"
                className="w-full h-full rounded-lg border border-slate-300"
                title="PDF Preview"
              />
            </div>
            
            {/* Modal Footer with Instructions */}
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <p className="text-sm text-slate-600 text-center">
                <strong>To save:</strong> Click the green "Download PDF" button above.
                <br />
                If that doesn't work, right-click the button and select "Save link as..."
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewGradedPaper;
